import { AttachableDocument } from "@dune/types";
import {
	Button,
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
	ResizableLayout,
} from "@dune/ui";
import { useMemo } from "react";
import { useGetBrokerDocuments } from "../../../../../api/queries/societies.queries";
import { TemplateSelectionDialog } from "../../../../../components/email/TemplateSelectionDialog";
import SignatureEditorDialog from "../../../../../email/SignatureEditorDialog";
import { EmailComposerForm } from "../../../../../email/components/EmailComposerForm";
import { useEmailComposer } from "../../../../../hooks/useEmailComposer";
import { useImageUpload } from "../../../../../hooks/useImageUpload";
import type { EmailComposerPanelProps } from "../../../../../types/panel.types";

export const BrokerEmailComposerPanel = ({
	initialData,
	draftId,
	disabled = false,
	replyTo,
	entityId,
	entityType = "BROKER",
	onClose,
}: EmailComposerPanelProps) => {
	const { uploadImage } = useImageUpload();

	const {
		control,
		watch,
		attachments,
		showSaveDraftPrompt,
		setShowSaveDraftPrompt,
		showCcBcc,
		setShowCcBcc,
		showTemplateDialog,
		setShowTemplateDialog,
		isSignatureDialogOpen,
		setIsSignatureDialogOpen,
		toSearch,
		ccSearch,
		bccSearch,
		templates,
		isLoadingTemplates,
		handleTemplateSelection,
		handleAttachmentAdd,
		handleAttachmentRemove,
		handleExistingDocumentsAdd,
		handleSendEmail,
		handleSaveDraft,
		handleDiscardDraft,
		getHandleEmailSearch,
		sendEmailMutation,
	} = useEmailComposer({
		initialData,
		draftId,
		entityId,
		entityType,
		replyTo,
		onClose,
		mapAttachmentToPayload: (att) => ({
			brokerDocumentId: att.entityDocumentId,
		}),
	});

	const { data: brokerDocuments, isLoading: isLoadingDocuments } =
		useGetBrokerDocuments(entityId);

	const brokerDocumentsAsAttachableDocuments: AttachableDocument[] =
		useMemo(() => {
			if (!brokerDocuments) return [];
			return brokerDocuments.map(
				(doc): AttachableDocument => ({
					id: doc.id.toString(),
					name: doc.file.name,
					bucket: doc.file.bucket,
					key: doc.file.key,
					mimeType: doc.file.mimeType,
					type: doc.type,
					createdAt: new Date(doc.createdAt),
					updatedAt: new Date(doc.updatedAt),
					status: doc.file.humanProcessingStatus,
				}),
			);
		}, [brokerDocuments]);

	const handleBrokerDocumentsAdd = async (documents: AttachableDocument[]) => {
		await handleExistingDocumentsAdd({
			documents,
			originalDocuments: brokerDocuments,
			findOriginal: (docs, id) => docs.find((doc) => doc.id.toString() === id),
			extractInfo: (doc) => ({
				bucket: doc.file.bucket,
				key: doc.file.key,
				name: doc.file.name,
				mimeType: doc.file.mimeType,
			}),
			extractId: (doc) => doc.id.toString(),
			entityName: "broker",
		});
	};

	return (
		<>
			<ResizableLayout.PanelContent className="p-4">
				<EmailComposerForm
					control={control}
					watch={watch}
					disabled={disabled}
					attachments={attachments}
					showCcBcc={showCcBcc}
					setShowCcBcc={setShowCcBcc}
					toSearch={toSearch}
					ccSearch={ccSearch}
					bccSearch={bccSearch}
					getHandleEmailSearch={getHandleEmailSearch}
					handleAttachmentAdd={handleAttachmentAdd}
					handleAttachmentRemove={handleAttachmentRemove}
					existingDocuments={brokerDocumentsAsAttachableDocuments}
					isLoadingDocuments={isLoadingDocuments}
					onExistingDocumentsAdd={handleBrokerDocumentsAdd}
					onImageUpload={uploadImage}
					onTemplateButtonClick={() => setShowTemplateDialog(true)}
					onSignatureButtonClick={() => setIsSignatureDialogOpen(true)}
					onSendClick={handleSendEmail}
					sendEmailMutation={sendEmailMutation}
					isLoadingTemplates={isLoadingTemplates}
				/>
			</ResizableLayout.PanelContent>

			<Dialog
				open={showSaveDraftPrompt}
				onOpenChange={(open: boolean) => {
					if (!open) {
						setShowSaveDraftPrompt(false);
					}
				}}
			>
				<DialogContent>
					<DialogHeader>
						<DialogTitle>Sauvegarder le brouillon ?</DialogTitle>
						<DialogDescription>
							Voulez-vous sauvegarder ce message en tant que brouillon ?
						</DialogDescription>
					</DialogHeader>
					<DialogFooter>
						<Button variant="outline" onClick={handleDiscardDraft}>
							Supprimer
						</Button>
						<Button onClick={handleSaveDraft}>Sauvegarder</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>

			<SignatureEditorDialog
				open={isSignatureDialogOpen}
				onOpenChange={setIsSignatureDialogOpen}
			/>

			<TemplateSelectionDialog
				open={showTemplateDialog}
				onOpenChange={setShowTemplateDialog}
				templates={templates || []}
				isLoading={isLoadingTemplates}
				onSelectTemplate={handleTemplateSelection}
			/>
		</>
	);
};
